import type { ProjectPhase } from '@/types'

export const STORED_SERVICE_RESULTS_FORMAT_VERSION = 2 as const

export const SERVICE_RESULT_CATEGORY_KEYS = [
  'organization',
  'visualization',
  'costs',
  'calendar',
  'administration',
] as const

export type ServiceResultCategoryKey = (typeof SERVICE_RESULT_CATEGORY_KEYS)[number]

export interface EffortCategory {
  unadjustedHours: number
  adjustmentPercentage: number
  adjustmentHours: number
  totalHours: number
}

export interface StoredServiceSubPhaseV2 {
  order: number
  remarks: string
  additionalHours: number
  totals: {
    unadjustedHours: number
    adjustmentHours: number
    additionalHours: number
    totalHours: number
  }
  effortCategories: Record<ServiceResultCategoryKey, EffortCategory>
}

export interface StoredServicePhaseV2 {
  order: number
  active: boolean
  percentage: {
    value: number | null
    min: number | null
    max: number | null
  }
  totals: {
    unadjustedHours: number
    adjustmentHours: number
    additionalHours: number
    totalHours: number
  }
  subPhases: StoredServiceSubPhaseV2[]
}

export interface StoredServiceResultsV2 {
  resultFormatVersion: typeof STORED_SERVICE_RESULTS_FORMAT_VERSION
  estimatedTotalHours: number
  totals: {
    unadjustedHours: number
    adjustmentHours: number
    additionalHours: number
    totalHours: number
  }
  phases: StoredServicePhaseV2[]
}

export interface PercentageRangeByPhase {
  [phaseOrder: string]: {
    min: number
    max: number
  }
}

export interface LegacyStoredServiceHours {
  [phaseOrder: string]: {
    [subPhaseOrder: string]: number[]
  }
}

interface BuildStoredServiceResultsV2Input {
  estimatedTotalHours: number
  phases: ProjectPhase[]
  percentageRangeForPhases?: PercentageRangeByPhase | null
  serviceHours?: LegacyStoredServiceHours | null
}

function toNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getCategoryHours(
  serviceHours: LegacyStoredServiceHours | null | undefined,
  phaseOrder: number,
  subPhaseOrder: number
): number[] {
  const raw = serviceHours?.[String(phaseOrder)]?.[String(subPhaseOrder)] ?? []
  return SERVICE_RESULT_CATEGORY_KEYS.map((_, index) => raw[index] ?? 0)
}

export function buildStoredServiceResultsV2({
  estimatedTotalHours,
  phases,
  percentageRangeForPhases,
  serviceHours,
}: BuildStoredServiceResultsV2Input): StoredServiceResultsV2 {
  const resultPhases = phases
    .filter((phase) => phase.active)
    .map((phase) => {
      const range = percentageRangeForPhases?.[String(phase.order)]
      let phaseUnadjustedHours = 0
      let phaseAdjustmentHours = 0
      let phaseAdditionalHours = 0

      const resultSubPhases = (phase.subPhases ?? []).map((subPhase) => {
        const categoryHours = getCategoryHours(serviceHours, phase.order, subPhase.order)
        const effortCategories = SERVICE_RESULT_CATEGORY_KEYS.reduce(
          (acc, key, index) => {
            const unadjustedHours = categoryHours[index]
            const adjustmentPercentage = subPhase.percentages?.[index] ?? 0
            const adjustmentHours = unadjustedHours * (adjustmentPercentage / 100)
            acc[key] = {
              unadjustedHours,
              adjustmentPercentage,
              adjustmentHours,
              totalHours: unadjustedHours + adjustmentHours,
            }
            return acc
          },
          {} as Record<ServiceResultCategoryKey, EffortCategory>
        )

        const unadjustedHours = Object.values(effortCategories).reduce(
          (sum, category) => sum + category.unadjustedHours,
          0
        )
        const adjustmentHours = Object.values(effortCategories).reduce(
          (sum, category) => sum + category.adjustmentHours,
          0
        )
        const additionalHours = subPhase.additionalEfforts ?? 0
        const totalHours = unadjustedHours + adjustmentHours + additionalHours

        phaseUnadjustedHours += unadjustedHours
        phaseAdjustmentHours += adjustmentHours
        phaseAdditionalHours += additionalHours

        return {
          order: subPhase.order,
          remarks: subPhase.remarks ?? '',
          additionalHours,
          totals: {
            unadjustedHours,
            adjustmentHours,
            additionalHours,
            totalHours,
          },
          effortCategories,
        }
      })

      return {
        order: phase.order,
        active: phase.active,
        percentage: {
          value: toNumberOrNull(phase.percentage),
          min: toNumberOrNull(range?.min),
          max: toNumberOrNull(range?.max),
        },
        totals: {
          unadjustedHours: phaseUnadjustedHours,
          adjustmentHours: phaseAdjustmentHours,
          additionalHours: phaseAdditionalHours,
          totalHours: phaseUnadjustedHours + phaseAdjustmentHours + phaseAdditionalHours,
        },
        subPhases: resultSubPhases,
      }
    })

  const unadjustedHours = resultPhases.reduce((sum, phase) => sum + phase.totals.unadjustedHours, 0)
  const adjustmentHours = resultPhases.reduce((sum, phase) => sum + phase.totals.adjustmentHours, 0)
  const additionalHours = resultPhases.reduce((sum, phase) => sum + phase.totals.additionalHours, 0)

  return {
    resultFormatVersion: STORED_SERVICE_RESULTS_FORMAT_VERSION,
    estimatedTotalHours,
    totals: {
      unadjustedHours,
      adjustmentHours,
      additionalHours,
      totalHours: unadjustedHours + adjustmentHours + additionalHours,
    },
    phases: resultPhases,
  }
}

export function isStoredServiceResultsV2(value: unknown): value is StoredServiceResultsV2 {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { resultFormatVersion?: unknown }).resultFormatVersion === STORED_SERVICE_RESULTS_FORMAT_VERSION
  )
}

export function hasLegacyStoredServiceResults(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false

  const results = value as Record<string, unknown>
  return 'defaultPhases' in results || 'percentageRangeForPhases' in results || 'serviceHours' in results
}
